import {MoreHoriz} from '@mui/icons-material';
import {Box, Dialog} from '@mui/material';
import {createFileRoute, useNavigate} from '@tanstack/react-router';
import {useEffect} from 'react';
import {useTranslation} from 'tolgee';

import {theme} from '~constants/theme';
import {isDevelopmentEnv} from '~utils/miscUtils';
import {
  confusionMatrixCellIdQueryParam,
  evaluationCellPredictedClassQueryParam,
  evaluationPipelineIdQueryParam,
  getRoute,
  imageBrowserActiveViewQueryParam,
  imagesOverviewViewModeQueryParam,
  keypointFinetuneQueryParam,
  Route as RouteEnum,
  useQueryParam,
} from '~utils/routeUtil';
import {useIsUnstableGallery} from '~hooks/useIsUnstableGallery';
import {InspectionType} from '~redux/types/models';

import {IconMenuItem} from '~components/common/menu-items/IconMenuItem';
import {ToolbarMenu} from '~components/common/toolbar/ToolbarMenu';
import {Transition} from '~components/common/Transition';
import {BaseLayout} from '~components/_layout/BaseLayout';
import {ImageBrowser} from '~components/models/finetune/image-browser/ImageBrowser';
import {ImageDetails} from '~components/models/finetune/image-browser/ImageDetails';
import {KeyPointDetectionFinetune} from '~components/models/finetune/keypoint-detection-finetune/KeyPointDetectionFinetune';
import {ModelFinetune} from '~components/models/finetune/ModelFinetune';
import {useIsCurrentModelAutomlEnabled} from '~components/models/model.hooks';
import {FinetuneThresholdProvider} from 'src/contexts/finetune/FinetuneThresholdContext';
import {ImagesCacheProvider} from 'src/contexts/ImagesCacheProvider';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ModelProvider} from 'src/contexts/ModelProvider';
import {useImageBrowser} from 'src/hooks/useImageBrowserContext';

export const Route = createFileRoute('/model/finetune/')({
  component: ModelFinetunePage,
});

function ModelFinetunePage() {
  const navigate = useNavigate();
  const {automlEnabled, isLoading} = useIsCurrentModelAutomlEnabled();
  const [activeConfusionMatrixCellId] = useQueryParam(confusionMatrixCellIdQueryParam);
  const [evaluationPipelineId] = useQueryParam(evaluationPipelineIdQueryParam);

  useEffect(() => {
    if (!isLoading && !automlEnabled && !isDevelopmentEnv() && !activeConfusionMatrixCellId && !evaluationPipelineId) {
      navigate({to: RouteEnum.notFound as any});
    }
  }, [automlEnabled, isLoading, activeConfusionMatrixCellId, evaluationPipelineId, navigate]);

  if (isLoading) {
    return null;
  }

  if (!automlEnabled && !isDevelopmentEnv() && !activeConfusionMatrixCellId && !evaluationPipelineId) {
    return null;
  }

  return (
    <ModelProvider mode='model-pages'>
      <ModelFinetunePageContent />
    </ModelProvider>
  );
}

function ModelFinetunePageContent() {
  const navigate = useNavigate();
  const {currentModel} = useCurrentModel();
  const {t} = useTranslation('models');
  const {handleImageBrowserModalClose} = useImageBrowser();
  const [activeConfusionMatrixCellId] = useQueryParam(confusionMatrixCellIdQueryParam);
  const [imageBrowserActiveView] = useQueryParam(imageBrowserActiveViewQueryParam);
  const [evaluationCellPredictedClass] = useQueryParam(evaluationCellPredictedClassQueryParam);
  const isUnstableGallery = useIsUnstableGallery();
  const [keypointFinetune] = useQueryParam(keypointFinetuneQueryParam);
  const [_viewMode, updateViewMode] = useQueryParam(imagesOverviewViewModeQueryParam);

  const isKeypointDetectionModel = currentModel?.inspectionType === InspectionType.keypointDetection;

  useEffect(() => {
    updateViewMode(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FinetuneThresholdProvider>
      <BaseLayout
        showBackButton
        customContentAndFooter
        titleActions={
          <Box
            sx={{
              ml: theme.spacing(0.5),
              '.MuiIconButton-root': {
                padding: 0,
                margin: 0,
                '&:hover': {
                  borderColor: theme.palette.primary.light,
                  color: theme.palette.maddox.globalblack,
                },
              },
            }}
          >
            <ToolbarMenu id='more-options-menu' icon={<MoreHoriz />}>
              <IconMenuItem
                text={t('deploymentSettings.title')}
                icon={null}
                onClick={() => navigate({to: getRoute(RouteEnum.modelFinetuneDeploymentSettings) as any})}
              />
            </ToolbarMenu>
          </Box>
        }
      >
        {isKeypointDetectionModel ? <KeyPointDetectionFinetune /> : <ModelFinetune />}
      </BaseLayout>

      <Dialog
        TransitionComponent={Transition}
        transitionDuration={500}
        open={
          !!activeConfusionMatrixCellId || !!evaluationCellPredictedClass || !!keypointFinetune || isUnstableGallery
        }
        onClose={() => handleImageBrowserModalClose()}
        fullScreen
        disableEnforceFocus
      >
        <ImagesCacheProvider mode='image-browser'>
          {imageBrowserActiveView === 'browser' ? <ImageBrowser /> : <ImageDetails />}
        </ImagesCacheProvider>
      </Dialog>
    </FinetuneThresholdProvider>
  );
}
